import { Component } from '@theme/component';

/**
 * Adds the selected add-ons to the cart as their own line items.
 *
 * Add-ons are separate products, so they can't be expressed as variants of
 * the kit and can't ride the product form's hidden `id` field. Instead, when
 * add-ons are selected this appends `items[N][...]` fields to the same form,
 * turning the single-item POST into a multi-item one. A kit can carry several
 * at once, so each selected add-on becomes its own line.
 *
 * `/cart/add.js` accepts an `items` array; when it's present the top-level
 * `id` is ignored, so the parent has to be restated as `items[0]`.
 *
 * The add-on line carries `properties[_parent]` pointing at the parent's
 * variant id. The leading underscore makes it a private property: it's kept
 * on the order for fulfilment but hidden from the customer in cart and
 * checkout. Cart-side cascade removal reads it to drop orphaned add-ons.
 *
 * @extends {Component}
 */
export default class CraftlyAddonPicker extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Horizon's `on:submit` binding builds its FormData before any listener we
    // could add here runs — injecting fields at submit time is too late, as the
    // POST goes out without them. So keep the fields in the DOM continuously
    // and refresh them whenever the selection changes; whenever Horizon
    // serialises the form, the right fields are already there.
    this.addEventListener('change', this.#syncFields);

    // Quantity add-ons (extra clay) are a radio row with nothing preselected,
    // and a native radio can't be unchecked once picked — so the customer
    // could never back out of it, unlike the single-variant checkboxes.
    // Clicking (or pressing Space on) the checked card clears the group.
    this.addEventListener('click', this.#onRadioClick);
    this.addEventListener('keydown', this.#onRadioKeydown);

    // Scent pickers and the quantity selector live outside this component but
    // feed items[0]. Listen on the document rather than on a cached form node:
    // the kit/size toggles re-render the section through the Section Rendering
    // API, which swaps in a brand-new <form>. A cached reference would survive
    // as a detached node and every injected field would land off-page, so the
    // POST would go out with no items[] at all.
    document.addEventListener('change', this.#syncFields);

    this.#syncFields();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#syncFields);
    this.removeEventListener('click', this.#onRadioClick);
    this.removeEventListener('keydown', this.#onRadioKeydown);
    document.removeEventListener('change', this.#syncFields);
    this.#clearInjectedFields();
  }

  /**
   * The card is a <label for> the hidden radio. Its click fires before the
   * browser forwards activation to the radio, so the radio's `checked` here
   * is still the state from before this click.
   * @param {MouseEvent} event
   */
  #onRadioClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('label[for]');
    if (!label) return;

    const radio = document.getElementById(label.htmlFor);
    if (!this.#isCheckedAddonRadio(radio)) return;

    // Stop the label from re-checking the radio, then clear it ourselves.
    event.preventDefault();
    this.#uncheck(radio);
  };

  /**
   * Keyboard parity with the checkbox add-ons: Space toggles off too.
   * @param {KeyboardEvent} event
   */
  #onRadioKeydown = (event) => {
    if (event.key !== ' ' || !this.#isCheckedAddonRadio(event.target)) return;

    event.preventDefault();
    this.#uncheck(event.target);
  };

  /**
   * @param {EventTarget | null} el
   * @returns {el is HTMLInputElement}
   */
  #isCheckedAddonRadio(el) {
    return (
      el instanceof HTMLInputElement &&
      el.type === 'radio' &&
      el.checked &&
      el.hasAttribute('data-craftly-addon') &&
      this.contains(el)
    );
  }

  /**
   * Setting `checked` from script fires no event, so dispatch `change` to
   * re-sync the cart fields and price like a real selection would.
   * @param {HTMLInputElement} radio
   */
  #uncheck(radio) {
    radio.checked = false;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * The add-to-cart form. The picker is a *sibling* of the buy-buttons block,
   * not a descendant of `product-form-component`, so `closest()` never finds
   * it — look it up by the id Liquid put on the block instead, and fall back
   * to the section's own product form.
   * @returns {HTMLFormElement | null}
   */
  #findForm() {
    const formId = this.dataset.formId;

    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
    }

    const section = this.closest('.shopify-section, [id^="shopify-section-"]') ?? document;
    const form = section.querySelector('form[data-type="add-to-cart-form"]');

    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * Every add-on variant id the customer picked, in the order rendered.
   *
   * A kit can offer several extras at once (extra clay, pens, matte varnish),
   * so this is a list. Radio groups contribute at most one id each — their
   * opt-out card carries an empty value, which is filtered out here — while
   * single-option add-ons are independent checkboxes.
   *
   * @returns {string[]}
   */
  get selectedVariantIds() {
    return this.#checkedAddonInputs()
      .map((input) => input.value?.trim())
      .filter((value) => Boolean(value));
  }

  /**
   * Every selected add-on card in this section.
   *
   * Scoped to the section, not to this element, so any sibling block can opt
   * into the same cart plumbing by marking its cards `data-craftly-addon`.
   *
   * @returns {HTMLInputElement[]}
   */
  #checkedAddonInputs() {
    const scope = this.closest('.shopify-section, [id^="shopify-section-"]') ?? this;

    return /** @type {HTMLInputElement[]} */ ([...scope.querySelectorAll('[data-craftly-addon]:checked')]);
  }

  /**
   * Mirrors the current selection into hidden fields on the product form.
   *
   * When an add-on is chosen the single-item payload (`id` + `quantity`) is
   * replaced by an `items[]` array: `/cart/add.js` ignores the top-level `id`
   * once `items` is present, so the kit has to be restated as `items[0]` —
   * along with its line item properties (the scent picks), which would
   * otherwise be lost.
   *
   * The kit's own `id`/`quantity` inputs stay in the form and are simply
   * ignored by the endpoint, so nothing needs disabling.
   */
  #syncFields = () => {
    // Re-resolve every time: the section may have been re-rendered since the
    // last sync, in which case the previous form node is detached.
    const form = this.#findForm();

    this.#syncPrice();

    if (!form) return;

    // Always start clean: a stale field would silently re-add an add-on the
    // customer has since deselected.
    this.#clearInjectedFields(form);

    const addonVariantIds = this.selectedVariantIds;
    if (addonVariantIds.length === 0) return;

    const parentVariantId = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="id"]'))?.value;
    if (!parentVariantId) return;

    const quantity = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="quantity"]'))?.value || '1';

    this.#injectField(form, 'items[0][id]', parentVariantId);
    this.#injectField(form, 'items[0][quantity]', quantity);

    for (const input of this.#checkedPropertyInputs(form)) {
      const key = input.name.slice('properties['.length, -1);
      this.#injectField(form, `items[0][properties][${key}]`, input.value);
    }

    // items[1..N] — one line per add-on, each linked back to the parent.
    addonVariantIds.forEach((variantId, index) => {
      const line = index + 1;

      this.#injectField(form, `items[${line}][id]`, variantId);
      this.#injectField(form, `items[${line}][quantity]`, quantity);
      this.#injectField(form, `items[${line}][properties][_parent]`, parentVariantId);
    });
  };

  /**
   * Rewrites the price summary to kit + selected add-on.
   *
   * The summary is rendered from Liquid at page load and knows nothing about
   * the add-on, so without this the price never moves when an add-on is
   * picked. `data-base-price` is the kit alone, so this recomputes from that
   * base each time rather than accumulating.
   */
  #syncPrice() {
    const addons = this.#checkedAddonInputs().reduce(
      (total, input) => total + (Number(input.dataset.addonPrice) || 0),
      0
    );

    // The compare-at moves by the same amount, so the discount shown stays the kit's own.
    for (const selector of ['[data-craftly-price]', '[data-craftly-compare-price]']) {
      const el = /** @type {HTMLElement | null} */ (document.querySelector(selector));
      if (!el) continue;

      const base = Number(el.dataset.basePrice);
      if (!Number.isFinite(base)) continue;

      el.textContent = this.#formatMoney(base + addons, el.dataset.moneyFormat);
    }
  }

  /**
   * Formats cents using the shop's money format.
   *
   * Only the amount placeholders Shopify defines are handled; anything else in
   * the format string (currency symbol, ISO code, surrounding text) is left as
   * written, so this follows the store's own formatting rather than assuming
   * EUR conventions.
   *
   * @param {number} cents
   * @param {string} [format]
   * @returns {string}
   */
  #formatMoney(cents, format) {
    const pattern = format || '{{amount}}';

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
      const amount = cents / 100;

      switch (name) {
        case 'amount':
          return amount.toLocaleString('lt-LT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        case 'amount_no_decimals':
          return amount.toLocaleString('lt-LT', { maximumFractionDigits: 0 });
        case 'amount_with_comma_separator':
          return amount.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return amount.toFixed(0);
        case 'amount_with_period_and_space_separator':
          return amount.toFixed(2);
        default:
          return '';
      }
    });
  }

  /**
   * The product form's line item property inputs that are actually active.
   * Radios and checkboxes only count when checked.
   * @param {HTMLFormElement} form
   * @returns {HTMLInputElement[]}
   */
  #checkedPropertyInputs(form) {
    // `form.elements`, not `querySelectorAll`: the palette, scent and varnish
    // pickers are sibling blocks outside the <form>, tied to it by their `form`
    // attribute. A descendant query misses them, and their choices would be
    // dropped from the kit line whenever an add-on is picked.
    const inputs = /** @type {HTMLInputElement[]} */ (
      [...form.elements].filter(
        (el) =>
          (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) && el.name.startsWith('properties[')
      )
    );

    return inputs.filter((input) => {
      if (input.disabled || !input.name.endsWith(']')) return false;
      if (input.type === 'radio' || input.type === 'checkbox') return input.checked;
      return input.value !== '';
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #injectField(form, name, value) {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    field.dataset.craftlyAddonField = 'true';
    form.appendChild(field);
  }

  /**
   * Removes previously injected fields. Scoped to the document rather than to
   * one form, so fields left behind in a form that has since been replaced by
   * a section re-render are cleaned up too.
   * @param {HTMLFormElement} [form]
   */
  #clearInjectedFields(form) {
    const root = form ?? document;
    root.querySelectorAll('[data-craftly-addon-field]').forEach((el) => el.remove());
  }
}

if (!customElements.get('craftly-addon-picker')) {
  customElements.define('craftly-addon-picker', CraftlyAddonPicker);
}
